import { useEffect } from 'react';

/**
 * GlobalRevealObserver — one shared IntersectionObserver that watches
 * EVERY DOM node with class `.am-reveal` (both those already present at
 * mount and any added later via a MutationObserver), and toggles
 * `.am-reveal-in` when they scroll into view.
 *
 * Drop <GlobalRevealObserver /> once at the App root and *any* element
 * anywhere in the tree can opt in to the fade-up simply by adding
 * className="am-reveal" — no per-component hook needed.
 *
 * The observer respects `prefers-reduced-motion: reduce` and disconnects
 * on unmount.
 */
export const GlobalRevealObserver = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      // Fallback: reveal everything immediately.
      document.querySelectorAll('.am-reveal').forEach((el) => el.classList.add('am-reveal-in'));
      return undefined;
    }
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      document.querySelectorAll('.am-reveal').forEach((el) => el.classList.add('am-reveal-in'));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('am-reveal-in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' },
    );

    const observeAll = (root = document) => {
      root.querySelectorAll('.am-reveal:not(.am-reveal-in)').forEach((el) => io.observe(el));
    };
    observeAll();

    // Also watch for nodes added later (route changes, async lists…).
    const mo = new MutationObserver((mutations) => {
      mutations.forEach((m) => {
        m.addedNodes.forEach((n) => {
          if (n.nodeType !== 1) return;
          if (n.classList?.contains('am-reveal') && !n.classList.contains('am-reveal-in')) {
            io.observe(n);
          }
          if (n.querySelectorAll) observeAll(n);
        });
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => { mo.disconnect(); io.disconnect(); };
  }, []);
  return null;
};

export default GlobalRevealObserver;
